import { AxiosError } from 'axios'

import { api } from '~/utils/api'

import { PagarmeListDTO } from '../types'
import {
  AvailableLicenseExtension,
  CreateLicenseExtensionDTO,
  LicenseExtension,
  LicenseExtensionPurchase,
  PurchaseLicenseExtensionDTO,
  UpdateLicenseExtensionDTO,
} from './types'

export function getLicenseExtensions(
  params: { page: number; size: number; name?: string },
  signal?: AbortSignal,
): Promise<PagarmeListDTO<LicenseExtension> | undefined> {
  return api
    .get<PagarmeListDTO<LicenseExtension>>('/admin/sales/license-extensions', { params, signal })
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      const isCanceled =
        (e as any)?.code === 'ERR_CANCELED' ||
        (e as any)?.name === 'CanceledError' ||
        (e as any)?.name === 'AbortError'
      if (isCanceled) return undefined
      throw new Error(e.response?.data.message)
    })
}

export function createLicenseExtension(
  payload: CreateLicenseExtensionDTO,
): Promise<LicenseExtension> {
  return api
    .post<LicenseExtension>('/admin/sales/license-extensions', payload)
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      throw new Error(e.response?.data.message)
    })
}

export function updateLicenseExtension(
  id: string,
  payload: UpdateLicenseExtensionDTO,
): Promise<LicenseExtension> {
  return api
    .patch<LicenseExtension>(`/admin/sales/license-extensions/${id}`, payload)
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      throw new Error(e.response?.data.message)
    })
}

export function deleteLicenseExtension(id: string): Promise<void> {
  return api
    .delete(`/admin/sales/license-extensions/${id}`)
    .then(() => undefined)
    .catch((e: AxiosError<APIError>) => {
      throw new Error(e.response?.data.message)
    })
}

export function getMyLicensePurchases(): Promise<LicenseExtensionPurchase[]> {
  return api
    .get<LicenseExtensionPurchase[]>('/sales/license-extensions/purchases')
    .then(({ data }) => data)
    .catch(() => [])
}

export function getAvailableLicenseExtensions(): Promise<AvailableLicenseExtension[]> {
  return api
    .get<AvailableLicenseExtension[]>('/sales/license-extensions')
    .then(({ data }) => data)
    .catch(() => [])
}

export function purchaseLicenseExtension(
  payload: PurchaseLicenseExtensionDTO,
): Promise<LicenseExtensionPurchase> {
  return api
    .post<LicenseExtensionPurchase>(
      '/personal-trainers/subscriptions/license-extensions',
      payload,
    )
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      throw new Error(e.response?.data.message)
    })
}

export function getLicenseExtensionPurchases(
  params: {
    page: number
    size: number
    licenseExtensionIds?: string[]
    name?: string
  },
  signal?: AbortSignal,
): Promise<PagarmeListDTO<LicenseExtensionPurchase> | undefined> {
  const { licenseExtensionIds, ...rest } = params
  const queryParams: Record<string, unknown> = { ...rest }
  if (licenseExtensionIds?.length) {
    queryParams['licenseExtensionId[]'] = licenseExtensionIds
  }
  return api
    .get<PagarmeListDTO<LicenseExtensionPurchase>>(
      '/admin/sales/license-extensions/purchases',
      { params: queryParams, signal },
    )
    .then(({ data }) => data)
    .catch((e: AxiosError<APIError>) => {
      const isCanceled =
        (e as any)?.code === 'ERR_CANCELED' ||
        (e as any)?.name === 'CanceledError' ||
        (e as any)?.name === 'AbortError'
      if (isCanceled) return undefined
      throw new Error(e.response?.data.message)
    })
}
